/** Record-changing actions, the pure half (plan D4, §3): which catalog action changes a
 *  field, the draft path its value travels on, which transitions a control offers, how it
 *  offers them (queue in the draft · "… now"), and the one-change body of a "… now".
 *
 *  D4: in edit mode every action QUEUES an entry in the draft — undoable, reviewed in the
 *  ledger, saved with everything else. While the draft is clean (view mode, or an edit
 *  session with nothing in it) the same control also offers the one-step "… now", which
 *  saves a changeset holding just that change. "… now" never runs over unsaved changes. */
import {
  actionPath,
  compileChangeset,
  draftValuesEqual,
  initialDraftState,
  type ChangesetExtras,
  type RecordKey,
} from '../draft/changeSet';
import type { RecordAction, RecordActionTransition, RecordChangeset, RecordField } from '../editor/types';

/** The draft path an action's value travels on: `action:<key>`, or the field itself for
 *  an action the wire records as a field change (cancel → booking_state). */
export function actionDraftPath(action: RecordAction): string {
  return action.change === 'action' ? actionPath(action.key) : action.field;
}

/** The catalog action that changes `field` (a field of kind `action`). */
export function actionOfField(field: RecordField, actions: readonly RecordAction[]): RecordAction | undefined {
  if (field.kind !== 'action' || !field.action) return undefined;
  return actions.find((a) => a.key === field.action);
}

/** The transitions that would change `current` — the state it is in is not offered. */
export function offeredTransitions(action: RecordAction, current: unknown): RecordActionTransition[] {
  return action.transitions.filter((t) => !draftValuesEqual(t.to, current));
}

export interface ActionModes {
  /** Adds an entry to the draft (opening the editor when it is not open). */
  queue: boolean;
  /** Saves a changeset with just this change. */
  now: boolean;
}

/** How a control offers an action:
 *  - edit mode queues; a clean draft also offers "… now";
 *  - view mode with a clean draft offers "… now" (an action without a one-step queues,
 *    opening the editor);
 *  - view mode with unsaved changes kept in the editor queues into them, opening it. */
export function actionModes({
  action,
  editing,
  dirty,
  canEdit,
}: {
  action: Pick<RecordAction, 'one_step'>;
  editing: boolean;
  dirty: boolean;
  /** The operator may open the editor (view mode → queue). */
  canEdit: boolean;
}): ActionModes {
  const oneStep = action.one_step !== false;
  if (editing) return { queue: true, now: !dirty && oneStep };
  if (dirty) return { queue: canEdit, now: false };
  return oneStep ? { queue: false, now: true } : { queue: canEdit, now: false };
}

/** The body of a "… now": exactly the change the draft would hold, compiled the same way
 *  (`compileChangeset`), plus the event policy and the answers its questions need. */
export function oneStepChangeset(
  change: { rec: RecordKey; path: string; from: unknown; to: unknown },
  extras: ChangesetExtras = {},
): RecordChangeset {
  return compileChangeset(
    { ...initialDraftState, entries: { [change.rec]: { [change.path]: { from: change.from, to: change.to } } } },
    extras,
  );
}
